import React from 'react';
import { DoneAll, Email, AppRegistration, HowToReg } from '@mui/icons-material';
import { SUMMARY_STATUS_PAGE_TEXT as pt } from './lang';
import { APPLICATION_STATUS_MODULE, APPLICATION_TODO_LIST } from './constants';
import { ArrowTopBar, type SidebarData } from '../../../../components';
import { APPLICATION_STATUS_TYPE } from '../../../../utils';

export type ApplicationStatusSideBarProps = {
  applicationStatus: APPLICATION_STATUS_TYPE;
  todoList: string[];
  visibleList: string[];
  defaultActivatedId: APPLICATION_STATUS_MODULE;
  onChangeActivated: (activatedId: APPLICATION_STATUS_MODULE) => void;
};

const isBadgeInvisible = (modules: string[], todoList: string[]): boolean =>
  !modules.some(module => todoList.includes(module));

export const ApplicationStatusSideBar: React.FC<
  ApplicationStatusSideBarProps
> = (props: ApplicationStatusSideBarProps) => {
  const {
    applicationStatus,
    todoList,
    visibleList,
    defaultActivatedId,
    onChangeActivated,
  } = props;

  const sidebarData: SidebarData[] = [
    {
      id: APPLICATION_STATUS_MODULE.APPLICATION,
      name: pt.APPLICATION,
      icon: <Email />,
      disabled: getStatusVisible(applicationStatus) <= 0,
      badgeInvisible: isBadgeInvisible(
        [
          APPLICATION_TODO_LIST.APPLICATION_FEE,
          APPLICATION_TODO_LIST.ACADEMIC_REFEREE,
          APPLICATION_TODO_LIST.SUPERVISOR_INVITATION,
        ],
        todoList,
      ),
      children: [
        {
          id: APPLICATION_STATUS_MODULE.APPLICATION_FEE_PAYMENT,
          name: pt.PAYMENT_STATUS,
          badgeInvisible: isBadgeInvisible(
            [APPLICATION_TODO_LIST.APPLICATION_FEE],
            todoList,
          ),
          visible: visibleList.includes(APPLICATION_TODO_LIST.APPLICATION_FEE),
        },
        {
          id: APPLICATION_STATUS_MODULE.ACADEMIC_REFEREE_STATUS,
          name: pt.ACADEMIC_REFEREE_STATUS,
          badgeInvisible: isBadgeInvisible(
            [APPLICATION_TODO_LIST.ACADEMIC_REFEREE],
            todoList,
          ),
          visible: visibleList.includes(APPLICATION_TODO_LIST.ACADEMIC_REFEREE),
        },
        {
          id: APPLICATION_STATUS_MODULE.SUPERVISOR_INVITATION,
          name: pt.SUPERVISOR_INVITATION,
          badgeInvisible: isBadgeInvisible(
            [APPLICATION_TODO_LIST.SUPERVISOR_INVITATION],
            todoList,
          ),
          visible: visibleList.includes(
            APPLICATION_TODO_LIST.SUPERVISOR_INVITATION,
          ),
        },
        {
          id: APPLICATION_STATUS_MODULE.RETURN_REMARKS,
          name: pt.RETURN_REMARKS,
          badgeInvisible: isBadgeInvisible(
            [APPLICATION_TODO_LIST.RETURN_REMARKS],
            todoList,
          ),
          visible: visibleList.includes(APPLICATION_TODO_LIST.RETURN_REMARKS),
        },
      ],
    },
    {
      id: APPLICATION_STATUS_MODULE.OFFER,
      name: pt.OFFER_STATUS,
      disabled: getStatusVisible(applicationStatus) <= 2,
      icon: <DoneAll />,
      badgeInvisible: isBadgeInvisible(
        [
          APPLICATION_TODO_LIST.OFFER_ACCEPTANCE,
          APPLICATION_TODO_LIST.REGISTRATION_FEE,
        ],
        todoList,
      ),
      children: [
        {
          id: APPLICATION_STATUS_MODULE.CONFIRM_OFFER,
          name: pt.OFFER_ACCEPTANCE,
          badgeInvisible: isBadgeInvisible(
            [APPLICATION_TODO_LIST.OFFER_ACCEPTANCE],
            todoList,
          ),
          visible: visibleList.includes(APPLICATION_TODO_LIST.OFFER_ACCEPTANCE),
        },
        // TODO disable = false when status is ACCEPT_OFFER(FIRM/CONDITIONAL)
        {
          id: APPLICATION_STATUS_MODULE.DEBIT_NOTE,
          name: pt.DEBIT_NOTE,
          badgeInvisible: isBadgeInvisible(
            [APPLICATION_TODO_LIST.REGISTRATION_FEE],
            todoList,
          ),
          visible: visibleList.includes(APPLICATION_TODO_LIST.REGISTRATION_FEE),
        },
      ],
    },
    {
      id: APPLICATION_STATUS_MODULE.REGISTRATION_PREPARATION,
      name: pt.REGISTRATION_PREPARATION,
      disabled: getStatusVisible(applicationStatus) <= 4,
      icon: <AppRegistration />,
      badgeInvisible: isBadgeInvisible(
        [
          APPLICATION_TODO_LIST.VISA_APPLICATION,
          APPLICATION_TODO_LIST.MEET_CONDITION,
          APPLICATION_TODO_LIST.QUALIFICATION_CHECK,
        ],
        todoList,
      ),
      children: [
        {
          id: APPLICATION_STATUS_MODULE.VISA_APPLICATION,
          name: pt.VISA_APPLICATION,
          badgeInvisible: isBadgeInvisible(
            [APPLICATION_TODO_LIST.VISA_APPLICATION],
            todoList,
          ),
          visible: visibleList.includes(APPLICATION_TODO_LIST.VISA_APPLICATION),
        },
        {
          id: APPLICATION_STATUS_MODULE.CONDITIONAL_OFFER_CONFIRMATION,
          name: pt.CONDITIONAL_OFFER_CONFIRMATION,
          badgeInvisible: isBadgeInvisible(
            [APPLICATION_TODO_LIST.MEET_CONDITION],
            todoList,
          ),
          visible: visibleList.includes(APPLICATION_TODO_LIST.MEET_CONDITION),
        },
        {
          id: APPLICATION_STATUS_MODULE.QUALIFICATION_CHECK,
          name: pt.QUALIFICATION_CHECK,
          badgeInvisible: isBadgeInvisible(
            [APPLICATION_TODO_LIST.QUALIFICATION_CHECK],
            todoList,
          ),
          visible: visibleList.includes(
            APPLICATION_TODO_LIST.QUALIFICATION_CHECK,
          ),
        },
        {
          id: APPLICATION_STATUS_MODULE.TUITION_FEE_PAYMENT,
          name: pt.TUITION_FEE_PAYMENT,
          badgeInvisible: isBadgeInvisible(
            [APPLICATION_TODO_LIST.TUITION_FEE],
            todoList,
          ),
          visible: visibleList.includes(APPLICATION_TODO_LIST.TUITION_FEE),
        },
      ],
    },
    {
      id: APPLICATION_STATUS_MODULE.REGISTRATION,
      name: pt.ONLINE_REGISTRATION,
      disabled: getStatusVisible(applicationStatus) <= 5,
      icon: <HowToReg />,
      badgeInvisible: isBadgeInvisible(
        [APPLICATION_TODO_LIST.REGISTRATION],
        todoList,
      ),
    },
  ];

  function getStatusVisible(status: APPLICATION_STATUS_TYPE): number {
    const statusVisibilityMap: Partial<
      Record<APPLICATION_STATUS_TYPE, number>
    > = {
      // can not see any module
      [APPLICATION_STATUS_TYPE.CREATED]: 0,
      [APPLICATION_STATUS_TYPE.WITHDRAWN]: 0,

      // can see APPLICATION - APPLICATION_FEE_PAYMENT and ACADEMIC_REFEREE module
      [APPLICATION_STATUS_TYPE.APPLICATION_FEE_PENDING]: 1,

      // can see APPLICATION - APPLICATION_FEE_PAYMENT and ACADEMIC_REFEREE module
      [APPLICATION_STATUS_TYPE.SUBMITTED]: 2,
      [APPLICATION_STATUS_TYPE.MODIFICATION_FROZEN]: 2,
      [APPLICATION_STATUS_TYPE.UNDER_SUPERVISOR]: 2,
      [APPLICATION_STATUS_TYPE.SUPERVISOR_NOMINATED]: 2,
      [APPLICATION_STATUS_TYPE.IN_NOMINATION_POOL]: 2,
      [APPLICATION_STATUS_TYPE.UNDER_DORPGS]: 2,
      [APPLICATION_STATUS_TYPE.UNDER_DSRC]: 2,
      [APPLICATION_STATUS_TYPE.DSRC_ASSESSED]: 2,
      [APPLICATION_STATUS_TYPE.UNDER_FRCSB]: 2,
      [APPLICATION_STATUS_TYPE.FRCSB_ASSESSED]: 2,
      [APPLICATION_STATUS_TYPE.UNDER_GS]: 2,
      [APPLICATION_STATUS_TYPE.GS_ASSESSED]: 2,
      [APPLICATION_STATUS_TYPE.PANEL_NOMINATED]: 2,
      [APPLICATION_STATUS_TYPE.RETURNED]: 2,
      [APPLICATION_STATUS_TYPE.UNDER_PANEL]: 2,
      [APPLICATION_STATUS_TYPE.OFFER_PROVISIONING]: 2, // GS confirmed the offer but waiting for the offer email sent to Applicant
      [APPLICATION_STATUS_TYPE.UNSUCCESSFUL]: 2, // failed to get offer

      // can see OFFER - OFFER_ACCEPTANCE module
      [APPLICATION_STATUS_TYPE.OFFERED]: 3,
      [APPLICATION_STATUS_TYPE.DECLINED]: 3,
      [APPLICATION_STATUS_TYPE.LAPSED]: 3, // offer lapsed

      // can see OFFER - DEBIT_NOTE module
      [APPLICATION_STATUS_TYPE.REGISTRATION_FEE_PENDING]: 4,

      // can see REGISTRATION_PREPARATION - VISA_APPLICATION, CONDITIONAL_OFFER_CONFIRMATION, QUALIFICATION
      [APPLICATION_STATUS_TYPE.CONDITION_MEET_LAPSED]: 5, // not met or overdue condition
      [APPLICATION_STATUS_TYPE.REGISTRATION_FEE_SETTLED]: 5,

      // can see ONLINE_REGISTRATION
      [APPLICATION_STATUS_TYPE.UNDER_REGISTRATION]: 6,
      [APPLICATION_STATUS_TYPE.REGISTERED]: 6,
    };

    return statusVisibilityMap[status] ?? 0;
  }

  const handleChangeActivated = (activatedId: string): void => {
    onChangeActivated(activatedId as APPLICATION_STATUS_MODULE);
  };

  const progressText =
    applicationStatus === APPLICATION_STATUS_TYPE.UNSUCCESSFUL
      ? 'Not Considered'
      : applicationStatus === APPLICATION_STATUS_TYPE.LAPSED
        ? 'Lapsed'
        : 'Application under Review';

  return (
    <ArrowTopBar
      sidebarData={sidebarData}
      onChangeActivated={handleChangeActivated}
      defaultActivatedId={defaultActivatedId}
      progressText={progressText}
    />
  );
};
